import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICONS } from '@constants';
import { fieldsHelper } from '@helpers';
import {
  CardSelectOption,
  DirectusReportFields,
  DirectusReportTypesResult,
  FileUploadItem,
  ReportFormField,
  SourceButtonOption,
} from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { EnvService, FilesService, LanguageService, ReportsService } from '@services';
import moment from 'moment';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  Subject,
  takeUntil,
  throttleTime,
} from 'rxjs';
import tzlookup from 'tz-lookup';
import { FieldName, FIELDS } from '../../core/constants/fields';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  animations: [
    trigger('inOutAnimation', [
      // Page-level states (form <-> success)
      state('form', style({ opacity: 1, transform: 'translateY(0)' })),
      state('success', style({ opacity: 1, transform: 'translateY(0)' })),

      // Group-level states
      state('visible', style({ opacity: 1, transform: 'translateY(0)' })),
      state('hidden', style({ opacity: 0, transform: 'translateY(20px)' })),

      // Page transitions
      transition('form => success', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })),
      ]),

      transition('success => form', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(20px)' })),
      ]),

      transition('void => form', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('0.3s 0.1s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),

      transition('void => success', [
        style({ opacity: 0, transform: 'translateY(-20px)' }),
        animate('0.3s 0.1s ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),

      // Group transitions
      transition('visible => hidden', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })), // Exit upward
      ]),

      transition('hidden => visible', [
        style({ opacity: 0, transform: 'translateY(-20px)' }), // Start from above
        animate('0.3s 0.1s ease-out', style({ opacity: 1, transform: 'translateY(0)' })), // Enter downward
      ]),

      transition('void => visible', [
        style({ opacity: 0, transform: 'translateY(-20px)' }), // Start from above
        animate('0.3s ease-out', style({ opacity: 1, transform: 'translateY(0)' })), // Enter downward
      ]),

      transition('visible => void', [
        animate('0.3s ease-in', style({ opacity: 0, transform: 'translateY(-20px)' })), // Exit upward
      ]),
    ]),
  ],
})
export class ReportComponent implements OnInit, OnDestroy {
  public isLoading = true;
  public isSubmitting = false;
  public success = false;
  public showUrgentNotice = false;
  public form: FormGroup = this.formBuilder.group({});
  public processedFields: ReportFormField[];
  public formFields: any = {};
  public reportTypeOptions: CardSelectOption[];
  public congressionalDistrictOptions: {
    id: string;
    text: string;
    value: string;
  }[];
  public sourceOptions: { [key: string]: SourceButtonOption } = {};
  public notices = ['location', 'source_options', 'media_files'];
  public formTitle = '';
  public selectedLanguage = 'en';
  public appUrl = '';

  public fieldTree: any[] = [];

  public getTranslatedLabel = fieldsHelper.getTranslatedLabel;
  public evaluateCondition = fieldsHelper.evaluateCondition;
  public findControl = fieldsHelper.findControl;
  public getFieldValue = fieldsHelper.getFieldValue;
  public isRequired = fieldsHelper.isRequired;
  public isHidden = fieldsHelper.isHidden;
  public getPlaceholder = fieldsHelper.getPlaceholder;

  private submitClick$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  public groupStates = new Map<string, 'visible' | 'hidden'>();
  public animationState = 'form';

  private fieldHiddenStates = new Map<string, boolean>();
  private pendingTypeKey: string | null = null;

  private readonly groupFlow = [
    { name: 'basic', mode: 'replace' },
    { name: 'verification', mode: 'replace' },
    { name: 'location_group', mode: 'cumulative' },
    { name: 'report_datetime_group', mode: 'cumulative' },
    {
      name: 'details',
      mode: 'cumulative',
      showWith: ['sources'], // Special case: details + sources show together
    },
    {
      name: 'sources',
      mode: 'cumulative',
      includeGroups: ['source_options_group'], // Include hidden helper group
    },
    {
      name: 'source_options_group',
      mode: 'cumulative',
      showWith: ['media_or_evidence'], // Show media_or_evidence when source_options_group is touched
    },
    { name: 'media_or_evidence', mode: 'cumulative' },
  ];

  public visibleGroups = new Set<string>(['basic']); // Start with basic visible
  public touchedGroups = new Set<string>();

  constructor(
    private reportsService: ReportsService,
    private filesService: FilesService,
    private formBuilder: FormBuilder,
    private cd: ChangeDetectorRef,
    private matIconReg: MatIconRegistry,
    private snackbar: MatSnackBar,
    private translate: TranslateService,
    private languageService: LanguageService,
    private envService: EnvService,
  ) {
    this.submitClick$
      .pipe(takeUntil(this.destroy$), throttleTime(500))
      .subscribe(() => this.handleSubmit());

    this.languageService.selectedLanguage$.pipe(takeUntil(this.destroy$)).subscribe((lang) => {
      this.selectedLanguage = lang;
    });
    this.appUrl = envService.environment.app_url;
    this.congressionalDistrictOptions = [];
  }

  ngOnInit() {
    this.matIconReg.setDefaultFontSetClass('material-symbols-outlined');
    this.pendingTypeKey = new URLSearchParams(window.location.search).get('type');
    this.reportsService.getDirectusReportTypes().subscribe({
      next: (reports: DirectusReportTypesResult) => {
        if (reports.data) {
          this.reportTypeOptions = reports.data
            .map((option) => ({
              value: `${option.id}`,
              title: option.name,
              description: option.description,
              icon: option.icon ?? undefined,
              sort: option.sort,
            }))
            .sort((a, b) => a.sort - b.sort);
          this.tryApplyDeepLink();
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
    this.reportsService.getDirectusReportFields().subscribe({
      next: (fields: DirectusReportFields) => {
        if (fields.data) {
          this.processedFields = fieldsHelper.processFields(fields.data);
          this.buildForm(this.processedFields);
          this.fieldTree = this.buildFieldTree(this.processedFields, null);
          this.tryApplyDeepLink();
          // TODO: Fix location
        }
      },
      error: (err) => {
        console.error(err);
      },
    });
    this.groupFlow.forEach((group) => {
      this.groupStates.set(group.name, group.name === 'basic' ? 'visible' : 'hidden');
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Deep-link: ?type=<semantic key> from the landing page preselects the report
  // type and advances the form past the picker. Idempotent: runs after both the
  // report types and the form fields have loaded (whichever finishes last applies it).
  private tryApplyDeepLink() {
    if (!this.pendingTypeKey) return;
    if (!this.reportTypeOptions || this.reportTypeOptions.length === 0) return;
    const basic = this.form.get('basic');
    const ctrl = this.form.get('basic.report_type');
    if (!basic || !ctrl) return;

    const titleKey = `report_types.${this.pendingTypeKey}.title`;
    const option = this.reportTypeOptions.find((o) => o.title === titleKey);
    if (!option) {
      this.pendingTypeKey = null;
      return;
    }

    ctrl.setValue(option.value);
    ctrl.markAsTouched();
    ctrl.markAsDirty();
    basic.markAsTouched();
    basic.markAsDirty();
    basic.updateValueAndValidity();
    this.pendingTypeKey = null;
  }

  private buildForm(processedFields: ReportFormField[]) {
    this.form = this.buildFormGroupRecursive(processedFields, null);
    this.setupConditionSubscriptions();
    this.setupGroupFlowSubscriptions();
    this.setupFormTitleSubscription();
    this.isLoading = false;
  }

  private buildFormGroupRecursive(
    processedFields: ReportFormField[],
    parentGroupName: string | null,
  ): FormGroup {
    const formGroup = new FormGroup({});

    // normalize group so undefined and null are treated the same
    const children = processedFields.filter((field) => (field.group ?? null) === parentGroupName);

    for (const child of children) {
      if (child.interface === 'group-raw') {
        const nestedFormGroup = this.buildFormGroupRecursive(processedFields, child.name);
        formGroup.addControl(child.name, nestedFormGroup);
      } else {
        const formControl = this.createControl(child);
        formGroup.addControl(child.name, formControl);

        if (child.name === 'source_options') {
          child.options?.choices?.forEach((choice: { text: string; value: string }) => {
            this.sourceOptions[choice.value] = {
              value: choice.value,
              text: choice.text,
              heading: 'reports.source_options_group.source_options.' + choice.value,
              repeatable: ['news_media', 'social_media', 'official_document'].includes(
                choice.value,
              ),
              icon: ICONS['sources'][choice.value] ?? '',
            };
          });
        }
      }
    }

    return formGroup;
  }

  private createControl(field: ReportFormField) {
    if (field.interface === 'group-raw') {
      return new FormGroup({});
    }

    const validators = [];

    if (field.required) {
      validators.push(Validators.required);
    }

    if (field.max_length) {
      validators.push(Validators.maxLength(field.max_length));
    }

    // ensure multi-select fields have an array default
    if (field.interface === 'select-multiple-checkbox') {
      const defaultVal = field.default_value ?? [];
      return new FormControl(defaultVal, validators);
    }

    if (field.interface === 'list') {
      return new FormArray([]);
    }

    return new FormControl(field.default_value ?? null, validators);
  }

  private buildFieldTree(
    processedFields: ReportFormField[],
    parentGroupName: string | null,
  ): any[] {
    const children = processedFields
      .filter((f) => (f.group ?? null) === parentGroupName)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));

    return children.map((child) => {
      if (child.interface === 'group-raw') {
        const nested = this.buildFieldTree(processedFields, child.name);
        return { ...child, children: nested };
      }
      return child;
    });
  }

  private initializeHiddenStates(): void {
    this.processedFields?.forEach((field) => {
      const isHidden = fieldsHelper.isHidden(field, this.form);
      this.fieldHiddenStates.set(field.name, isHidden);
    });
  }

  private updateConditionalValidators() {
    this.processedFields.forEach((field) => {
      const currentlyHidden = fieldsHelper.isHidden(field, this.form);
      const previouslyHidden = this.fieldHiddenStates.get(field.name) ?? false;

      if (!previouslyHidden && currentlyHidden) {
        this.resetFieldControl(field);
      }

      this.fieldHiddenStates.set(field.name, currentlyHidden);

      if (!field.conditions?.length) return;

      const control = this.findControl(field.name, this.form);
      if (!control) return;

      const shouldBeRequired = this.isRequired(field, this.form);
      const hasRequired = control.hasValidator(Validators.required);

      if (shouldBeRequired && !hasRequired) {
        control.addValidators(Validators.required);
        control.updateValueAndValidity({ emitEvent: false });
      } else if (!shouldBeRequired && hasRequired && !field.required) {
        control.removeValidators(Validators.required);
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private resetFieldControl(field: ReportFormField): void {
    const control = this.findControl(field.name, this.form);
    if (control) {
      const resetValue = this.getResetValueForField(field);
      control.reset(resetValue);
      control.markAsUntouched();
      control.markAsPristine();
    }
  }

  private getResetValueForField(field: ReportFormField): any {
    switch (field.interface) {
      case 'select-multiple-checkbox':
      case 'list':
      case 'files':
        return [];
      case 'datetime':
      case 'input':
      case 'input-multiline':
      case 'select-dropdown':
      case 'select-radio':
        return field.default_value ?? null;
      default:
        return field.default_value ?? null;
    }
  }

  private setupConditionSubscriptions() {
    // Get unique field names referenced in conditions
    const referencedFields = new Set<string>();
    this.processedFields.forEach((field) => {
      field.conditions?.forEach((condition) => {
        const fields = this.extractReferencedFields(condition.rule);
        fields.forEach((f) => referencedFields.add(f));
      });
    });

    // Subscribe to changes in referenced fields
    referencedFields.forEach((fieldName) => {
      const control = this.findControl(fieldName, this.form);
      if (control) {
        control.valueChanges
          .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe(() => {
            this.updateConditionalValidators();
            // Trigger change detection to re-evaluate conditions
            this.cd.detectChanges();
          });
      }
    });

    this.initializeHiddenStates();
  }

  private setupGroupFlowSubscriptions() {
    this.groupFlow.forEach((groupConfig, index) => {
      const formGroup = this.form.get(groupConfig.name);
      if (formGroup) {
        // Watch for touched/dirty state
        formGroup.statusChanges
          .pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.destroy$))
          .subscribe(() => {
            this.handleGroupInteraction(groupConfig, index);
          });
      }
    });
  }

  private setupFormTitleSubscription() {
    const reportTypeControl = this.form.get('basic.report_type');
    if (reportTypeControl) {
      reportTypeControl.valueChanges
        .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe((selectedValue) => {
          if (selectedValue && this.reportTypeOptions) {
            const selectedOption = this.reportTypeOptions.find(
              (option) => option.value === selectedValue,
            );
            this.formTitle = selectedOption?.title || '';
            if (selectedOption?.title === 'report_types.election_interference.title') {
              this.fetchCongressionalDistricts();
            } else {
              this.congressionalDistrictOptions = [];
            }
          } else {
            this.formTitle = '';
            this.congressionalDistrictOptions = [];
          }
          this.cd.detectChanges();
        });
    }
  }

  private fetchCongressionalDistricts() {
    if (this.congressionalDistrictOptions.length === 0) {
      this.reportsService.getCongressionalDistricts().subscribe({
        next: (districts) => {
          if (districts.data) {
            this.congressionalDistrictOptions = districts.data.map((district) => ({
              id: district.id,
              text: district.display_name,
              value: district.display_name,
            }));
            this.cd.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error fetching congressional districts:', err);
        },
      });
    }
  }

  private handleGroupInteraction(groupConfig: any, index: number) {
    const formGroup = this.form.get(groupConfig.name);
    if (!formGroup || (!formGroup.touched && !formGroup.dirty)) return;

    this.touchedGroups.add(groupConfig.name);

    if (groupConfig.mode === 'replace') {
      // Sequential animation: hide current, then show next
      this.animateGroupTransition(groupConfig, index);
    } else if (groupConfig.mode === 'cumulative') {
      // Show next group(s) cumulatively with entrance animation
      this.animateCumulativeGroups(groupConfig, index);
    }
  }

  private async animateGroupTransition(groupConfig: any, index: number) {
    // Hide current group
    this.groupStates.set(groupConfig.name, 'hidden');
    this.visibleGroups.delete(groupConfig.name);

    // Wait for exit animation
    await this.delay(300);

    // Show next group
    const nextGroup = this.groupFlow[index + 1];
    if (nextGroup) {
      this.visibleGroups.add(nextGroup.name);
      this.groupStates.set(nextGroup.name, 'visible');

      if (nextGroup.includeGroups) {
        nextGroup.includeGroups.forEach((group) => {
          this.visibleGroups.add(group);
          this.groupStates.set(group, 'visible');
        });
      }
    }

    this.updateConditionalValidators();

    Promise.resolve().then(() => this.cd.detectChanges());
  }

  private animateCumulativeGroups(groupConfig: any, index: number) {
    const nextGroup = this.groupFlow[index + 1];
    if (nextGroup) {
      this.visibleGroups.add(nextGroup.name);
      this.groupStates.set(nextGroup.name, 'visible');

      if (nextGroup.showWith) {
        nextGroup.showWith.forEach((group) => {
          this.visibleGroups.add(group);
          this.groupStates.set(group, 'visible');
        });
      }

      if (nextGroup.includeGroups) {
        nextGroup.includeGroups.forEach((group) => {
          this.visibleGroups.add(group);
          this.groupStates.set(group, 'visible');
        });
      }
    }

    if (groupConfig.showWith) {
      groupConfig.showWith.forEach((group: any) => {
        this.visibleGroups.add(group);
        this.groupStates.set(group, 'visible');
      });
    }

    // Show form footer when source_options_group is touched
    if (groupConfig.name === 'source_options_group') {
      this.visibleGroups.add('form-footer');
    }

    this.updateConditionalValidators();

    Promise.resolve().then(() => this.cd.detectChanges());
  }

  public getGroupAnimationState(groupName: string): string {
    return this.groupStates.get(groupName) || 'hidden';
  }

  public getFieldMaxLength(field: FieldName) {
    return FIELDS[field];
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private extractReferencedFields(rule: any): string[] {
    const fields: string[] = [];

    if (rule._and) {
      rule._and.forEach((r: any) => {
        fields.push(...this.extractReferencedFields(r));
      });
    }

    if (rule._or) {
      rule._or.forEach((r: any) => {
        fields.push(...this.extractReferencedFields(r));
      });
    }

    const fieldName = Object.keys(rule).find((k) => !k.startsWith('_'));
    if (fieldName) {
      fields.push(fieldName);
    }

    return [...new Set(fields)];
  }

  public getGroupFromFieldTree(name: string) {
    return this.fieldTree.find((field) => field.name === name);
  }

  public setDate($event: any) {
    const datetimeControl = this.form.get('report_datetime_group.report_datetime');
    if ($event.value != 'unknown') {
      datetimeControl?.setValue(new Date());
    } else {
      datetimeControl?.reset();
    }
  }

  private showMessage(message: string, type: string) {
    this.snackbar.open(message, this.translate.instant('form.close'), {
      panelClass: [type],
      duration: 6000,
    });
  }

  public buildPayload(control: { [key: string]: any }) {
    let payload: { [key: string]: any } = {};
    Object.keys(control).forEach((key) => {
      const currControl = control[key];

      if (
        !Array.isArray(currControl) &&
        !(currControl instanceof Date) &&
        !(currControl instanceof moment) &&
        currControl instanceof Object &&
        key !== 'location' &&
        key !== 'verification'
      ) {
        payload = { ...payload, ...this.buildPayload(currControl) };
      } else if (key === 'verification') {
        payload = {
          ...payload,
          is_verified: currControl['is_verified']['isVerified'],
          phone_number: currControl['is_verified']['phoneNumber'],
        };
      } else {
        payload[key] = currControl;
      }
    });

    return payload;
  }

  public toggleForm(enable: boolean) {
    if (enable) {
      this.form.enable();
      this.isSubmitting = false;
    } else {
      this.form.disable();
      this.isSubmitting = true;
    }
  }

  public preventSubmitIncaseTheresNoBackendValidation() {
    // TODO
    this.form.markAllAsTouched();
  }

  public onSubmit() {
    this.preventSubmitIncaseTheresNoBackendValidation();
    this.isSubmitting = true;
    this.form.disable();
    this.submitClick$.next();
  }

  public async handleSubmit() {
    this.toggleForm(false);

    const payload = this.buildPayload(this.form.value);

    if (this.form.invalid) {
      console.error('Form is invalid: ', this.form.errors);
      this.toggleForm(true);
      return;
    }
    // TODO: Dynamically get all fields that accept files
    // For now, assume only `media_files` field accepts files
    payload['media_files'] = null;
    const fileControlValue = this.form.get('media_or_evidence.media_files')?.value;

    // This is null if user did not select a report date and time
    if (!payload['report_datetime']) {
      payload['report_datetime'] = new Date();
    }

    // Update report_datetime based on location timezone
    let timezone: string | null = null;
    if (payload['location']) {
      const location = payload['location'];
      timezone = tzlookup(location.coordinates[1], location.coordinates[0]); // (lat, lng) from GeoJSON [lng, lat]
    }
    if (timezone) {
      // Extract the date/time components from the original UTC datetime
      const originalDate = new Date(payload['report_datetime']);
      const year = originalDate.getUTCFullYear();
      const month = String(originalDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(originalDate.getUTCDate()).padStart(2, '0');
      const hours = String(originalDate.getUTCHours()).padStart(2, '0');
      const minutes = String(originalDate.getUTCMinutes()).padStart(2, '0');

      // Create a date string without timezone info, then interpret it in the target timezone
      const localDateStr = `${year}-${month}-${day}T${hours}:${minutes}:00`;

      // Get UTC offset for the target timezone at this date
      const tempDate = new Date(localDateStr + 'Z'); // Treat as UTC temporarily
      const utcTime = tempDate.getTime();
      const tzTime = new Date(tempDate.toLocaleString('en-US', { timeZone: timezone })).getTime();
      const offset = utcTime - tzTime; // Offset in ms (positive if timezone is behind UTC)

      // Apply offset: if datetime is "10:30 in America/New_York", convert to UTC
      const finalUtcDate = new Date(utcTime + offset);
      payload['report_datetime'] = finalUtcDate.toISOString();
    }

    if (!fileControlValue || (Array.isArray(fileControlValue) && fileControlValue.length === 0)) {
      if (payload['report_type'] !== '7') {
        payload['congressional_district'] = null;
      }
      // No files are to be uploaded
      this.submitReport(payload);
    } else {
      this.submitReportWithFiles(payload, fileControlValue);
    }
  }

  private submitReport(payload: { [key: string]: any }) {
    payload['media_files'] = null;

    this.reportsService.createReport(payload).subscribe({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      next: (response) => {
        if (payload['report_datetime_is_exact'] === 'now') {
          this.showUrgentNotice = true;
        }
        this.handleSuccess();
      },
      error: (error) => this.handleError(error),
    });
  }

  private submitReportWithFiles(payload: { [key: string]: any }, fileControlValue: File[]) {
    const files: FileUploadItem[] = (fileControlValue as File[]).map((file) => ({ file }));

    this.filesService
      .uploadFiles(files)
      .pipe(
        concatMap((fileData) => {
          const fileIds = Array.isArray(fileData.data)
            ? fileData.data.map((file) => file.id)
            : fileData.data.id
            ? [fileData.data.id]
            : [];

          payload['media_files'] = {
            create: fileIds.map((id) => ({
              reports_id: '+',
              directus_files_id: {
                id: id,
              },
            })),
            update: [],
            delete: [],
          };

          return this.reportsService.createReport(payload);
        }),
      )
      .subscribe({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        next: (response) => {
          if (payload['report_datetime_is_exact'] === 'now') {
            this.showUrgentNotice = true;
          }
          this.handleSuccess();
        },
        error: (error) => this.handleError(error),
      });
  }

  public isGroupVisible(groupName: string): boolean {
    return this.visibleGroups.has(groupName);
  }

  public isFormFooterVisible(): boolean {
    return this.visibleGroups.has('form-footer');
  }

  private handleSuccess() {
    this.isSubmitting = false;
    this.showMessage(this.translate.instant('form.submitted'), 'success');

    setTimeout(() => {
      this.success = true;
      this.animationState = 'success';
    }, 300);
  }

  private handleError(error: any) {
    this.toggleForm(true);
    console.error(error);
    this.showMessage(this.translate.instant('form.errors.submit_error'), 'error');
  }

  public handleSubmitNew() {
    this.groupStates.clear();
    this.visibleGroups.clear();
    this.touchedGroups.clear();

    this.toggleForm(true);

    this.visibleGroups.add('basic');
    this.groupFlow.forEach((group) => {
      this.groupStates.set(group.name, group.name === 'basic' ? 'visible' : 'hidden');
    });

    this.buildForm(this.processedFields);

    this.cd.detectChanges();

    setTimeout(() => {
      this.success = false;
      this.showUrgentNotice = false;
      this.animationState = 'form';
    }, 300);
  }
}
